<template>
  <aside class="element-picker-panel" aria-labelledby="element-picker-panel-title">
    <div class="element-picker-panel__header">
      <h3 id="element-picker-panel-title" class="text-[13px]/none font-bold whitespace-nowrap text-heading">
        Selected elements
      </h3>
      <span class="element-picker-count">{{ items.length }}</span>
      <span class="flex-1" />
      <template v-if="items.length">
        <Button
          label="Advanced"
          text
          size="small"
          class="element-picker-text-button"
          :aria-pressed="advanced"
          title="Show and edit the CSS selector for each element"
          @click="advanced = !advanced" />
        <Button label="Clear all" text size="small" class="element-picker-text-button" @click="emit('clear')" />
      </template>
    </div>

    <div class="flex min-h-0 flex-1 flex-col overflow-auto px-2 pb-3">
      <div v-if="!items.length" class="element-picker-empty">
        <ViewfinderCircleIcon class="size-6" aria-hidden="true" />
        <p class="text-[13px]/[1.3] font-semibold text-heading">Nothing selected yet</p>
        <p class="text-[12.5px]/[1.45]">Hover the page and click an element to add it here.</p>
      </div>
      <ol v-else class="flex flex-col gap-0.5">
        <li
          v-for="item in items"
          :key="item.key"
          class="element-picker-item"
          @mouseenter="emit('hover', item.key)"
          @mouseleave="emit('hover', null)">
          <span class="element-picker-badge" aria-hidden="true">{{ item.number }}</span>
          <div class="flex min-w-0 flex-col gap-1">
            <p class="text-[13px]/5 font-semibold text-heading">
              <span class="sr-only">{{ item.number }}. </span>{{ item.label }}
            </p>
            <p v-if="item.text" class="line-clamp-2 text-[12.5px]/[1.4] text-copy">{{ item.text }}</p>
            <p v-if="item.note" class="text-xs/[1.4] text-muted">{{ item.note }}</p>
            <button
              v-if="item.canSelectParent"
              type="button"
              class="element-picker-parent"
              @click="emit('selectParent', item.key)">
              <ArrowUpIcon class="size-3.5" aria-hidden="true" />Select the area around this
            </button>
            <div v-if="advanced" class="mt-0.5 flex flex-col gap-1">
              <InputText
                :model-value="drafts[item.key] ?? item.selector"
                size="small"
                fluid
                spellcheck="false"
                :invalid="Boolean(errors[item.key])"
                :aria-label="`CSS selector for element ${item.number}`"
                class="element-picker-selector"
                @update:model-value="drafts[item.key] = $event ?? ''"
                @keydown.enter.prevent="commit(item)"
                @blur="commit(item)" />
              <p v-if="errors[item.key]" class="text-xs text-danger">{{ errors[item.key] }}</p>
            </div>
          </div>
          <div class="flex flex-col gap-0.5">
            <button
              type="button"
              class="element-picker-icon-button element-picker-icon-button--danger"
              :aria-label="`Remove ${item.label} ${item.number}`"
              title="Remove"
              @click="emit('remove', item.key)">
              <XMarkIcon class="size-[15px]" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="element-picker-icon-button"
              :aria-label="`Scroll to ${item.label} ${item.number}`"
              :title="item.onPage ? 'Scroll to element' : 'Not on this page'"
              :disabled="!item.onPage"
              @click="emit('locate', item.key)">
              <ViewfinderCircleIcon class="size-[15px]" aria-hidden="true" />
            </button>
          </div>
        </li>
      </ol>
    </div>

    <p class="element-picker-panel__footer">
      Click a selected element on the page to remove it. Hold <kbd>{{ altKey }}</kbd> to click links without selecting.
    </p>
  </aside>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { ArrowUpIcon, ViewfinderCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import type { ElementPickerItem } from './elementPickerItem'

defineProps<{ items: ElementPickerItem[]; altKey: string }>()
const emit = defineEmits<{
  hover: [key: string | null]
  remove: [key: string]
  locate: [key: string]
  selectParent: [key: string]
  updateSelector: [key: string, selector: string]
  clear: []
}>()

const advanced = ref(false)
const drafts = reactive<Record<string, string>>({})
const errors = reactive<Record<string, string>>({})

function commit(item: ElementPickerItem) {
  const draft = drafts[item.key]
  if (draft === undefined) {
    return
  }
  const selector = draft.trim()
  if (selector === item.selector) {
    delete drafts[item.key]
    delete errors[item.key]
    return
  }
  let valid = selector.length > 0
  try {
    document.createDocumentFragment().querySelector(selector)
  } catch {
    valid = false
  }
  if (!valid) {
    errors[item.key] = 'Enter a valid CSS selector.'
    return
  }
  delete drafts[item.key]
  delete errors[item.key]
  emit('updateSelector', item.key, selector)
}
</script>

<style scoped>
.element-picker-panel {
  display: flex;
  width: 320px;
  flex-shrink: 0;
  flex-direction: column;
  border-left: 1px solid var(--rm-line);
  background: var(--rm-app-card);
}
.element-picker-panel__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 12px 10px 16px;
}
.element-picker-panel__footer {
  padding: 12px 16px;
  border-top: 1px solid var(--rm-line);
  color: var(--rm-muted);
  font-size: 12px;
  line-height: 1.45;
}
.element-picker-panel__footer kbd {
  padding: 1px 5px;
  border-radius: 4px;
  background: var(--rm-overlay);
  color: var(--rm-ink);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}
.element-picker-count {
  display: inline-flex;
  min-width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  padding: 0 6px;
  border-radius: 9999px;
  background: var(--element-picker-tint);
  color: var(--element-picker-ink);
  font-size: 11.5px;
  font-weight: 700;
}
.element-picker-text-button.p-button {
  flex-shrink: 0;
  padding: 6px 8px;
  font-size: 12.5px;
  white-space: nowrap;
}
.element-picker-text-button.p-button[aria-pressed='true'] {
  background: var(--rm-overlay);
}
.element-picker-empty {
  display: flex;
  margin: 8px;
  padding: 28px 20px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  border: 1px dashed var(--rm-line-strong);
  border-radius: 8px;
  color: var(--rm-muted);
  text-align: center;
}
.element-picker-item {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  gap: 10px;
  padding: 10px 8px;
  border-radius: 8px;
  transition: background-color 120ms;
}
.element-picker-item:hover {
  background: var(--element-picker-tint);
}
.element-picker-badge {
  height: 20px;
  border-radius: 5px;
  background: var(--element-picker-pink);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
}
.element-picker-parent {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  color: var(--rm-primary);
  font-size: 12px;
  font-weight: 600;
}
.element-picker-parent:hover {
  color: var(--rm-primary-strong);
  text-decoration: underline;
}
.element-picker-selector.p-inputtext {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 11.5px;
}
.element-picker-icon-button {
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: var(--rm-muted);
  transition:
    background-color 120ms,
    color 120ms;
}
.element-picker-icon-button:hover:not(:disabled) {
  background: var(--rm-overlay);
  color: var(--rm-heading);
}
.element-picker-icon-button--danger:hover:not(:disabled) {
  background: var(--rm-danger-soft);
  color: var(--rm-danger);
}
.element-picker-icon-button:disabled {
  color: var(--rm-faint);
  cursor: default;
  opacity: 0.6;
}
</style>
